import React from 'react'

// Individual information section components following Single Responsibility Principle

export function PCO2InfoSection() {
  return (
    <div style={{
      backgroundColor: '#e7f3ff',
      padding: '1.5rem',
      borderRadius: '8px',
      border: '1px solid #b3d9ff'
    }}>
      <h3 style={{ color: '#2c3e50', marginTop: '0' }}>What is pCO₂?</h3>
      <p style={{ marginBottom: '1rem', lineHeight: '1.6' }}>
        pCO₂ stands for the partial pressure of carbon dioxide in seawater. It tells us how much CO₂ 
        is dissolved in the ocean compared to the atmosphere. <strong>If seawater pCO₂ is lower than the air, 
        the ocean absorbs CO₂; if it's higher, the ocean releases CO₂ back.</strong> Tracking pCO₂ helps us 
        understand the ocean's role as both a sink and source of carbon.
      </p>
      <ul style={{ paddingLeft: '1.5rem', lineHeight: '1.6' }}>
        <li><strong>Higher pCO₂:</strong> Ocean releases CO₂ to atmosphere</li>
        <li><strong>Lower pCO₂:</strong> Ocean absorbs CO₂ from atmosphere</li>
        <li><strong>~400 μatm:</strong> Equilibrium with current atmospheric CO₂</li>
      </ul>
    </div>
  )
}

export function MonitoringImportanceSection() {
  return (
    <div style={{
      backgroundColor: '#f8f9fa',
      padding: '1.5rem',
      borderRadius: '8px',
      border: '1px solid #e9ecef'
    }}>
      <h3 style={{ color: '#2c3e50', marginTop: '0' }}>Why Monitor Ocean CO₂?</h3>
      <p style={{ marginBottom: '1rem', lineHeight: '1.6' }}>
        The ocean absorbs about 25% of human CO₂ emissions, making it crucial for climate regulation. 
        Real-time monitoring helps us understand:
      </p>
      <ul style={{ paddingLeft: '1.5rem', lineHeight: '1.6' }}>
        <li>Ocean acidification trends</li>
        <li>Carbon cycle dynamics</li>
        <li>Climate change impacts on marine ecosystems</li>
        <li>Effectiveness of carbon removal technologies</li>
      </ul>
    </div>
  )
}

export function DataSourceSection() {
  return (
    <div style={{
      backgroundColor: '#fff3cd',
      padding: '1.5rem',
      borderRadius: '8px',
      border: '1px solid #ffc107'
    }}>
      <h3 style={{ color: '#2c3e50', marginTop: '0' }}>Data Source</h3>
      <p style={{ marginBottom: '1rem', lineHeight: '1.6' }}>
        This data comes from autonomous monitoring buoys deployed in the Atlantic Ocean 
        as part of the <strong>Ocean Alk-Align</strong> research project investigating 
        Ocean Alkalinity Enhancement for carbon dioxide removal.
      </p>
      <p style={{ margin: 0 }}>
        <strong>Location:</strong> Atlantic Canada waters<br />
        <strong>Sensor:</strong> ProCV CO₂ monitoring system<br />
        <strong>Update Frequency:</strong> Real-time measurements
      </p>
    </div>
  )
}

export function OAEInfoSection() {
  return (
    <div style={{
      backgroundColor: '#f0f8ff',
      padding: '1.5rem',
      borderRadius: '8px',
      border: '1px solid #b3d9ff'
    }}>
      <h3 style={{ color: '#2c3e50', marginTop: '0' }}>About Ocean Alkalinity Enhancement</h3>
      <p style={{ marginBottom: '1rem', lineHeight: '1.6' }}>
        Ocean Alkalinity Enhancement (OAE) is a marine Carbon Dioxide Removal technology that accelerates 
        the ocean's natural carbon absorption process. By adding alkaline minerals to seawater, OAE:
      </p>
      <ul style={{ paddingLeft: '1.5rem', marginBottom: '1rem', lineHeight: '1.6' }}>
        <li><strong>Increases pH:</strong> Makes seawater more alkaline, enhancing CO₂ absorption</li>
        <li><strong>Stores Carbon:</strong> Converts atmospheric CO₂ to stable bicarbonate ions</li>
        <li><strong>Permanent Removal:</strong> Carbon remains stored in the ocean for centuries</li>
        <li><strong>Scalable Solution:</strong> Potential for large-scale climate impact</li>
      </ul>
      <p style={{ margin: 0, lineHeight: '1.6' }}>
        Real-time monitoring of ocean chemistry, like the pCO₂ data shown above, is essential for 
        understanding the effectiveness and environmental safety of OAE deployments.
      </p>
    </div>
  )
}

export function ResearchPartnersSection() {
  return (
    <div style={{
      backgroundColor: '#f8f9fa',
      padding: '1.5rem',
      borderRadius: '8px',
      border: '1px solid #dee2e6',
      marginBottom: '2rem'
    }}>
      <h3 style={{ color: '#2c3e50', marginTop: '0', marginBottom: '1rem', textAlign: 'center' }}>
        Research Partners & Real-World Application
      </h3>
      <p style={{ marginBottom: '1.5rem', lineHeight: '1.6', textAlign: 'center' }}>
        <strong>Halifax Harbour Pilot:</strong> Planetary Technologies is conducting Ocean Alkalinity 
        Enhancement deployment in Halifax Harbour, Nova Scotia. Our monitoring data contributes to the 
        <strong> Carbon to Sea Initiative</strong>, a collaborative effort to develop sustainable marine 
        carbon removal technologies.
      </p>
      <div style={{ 
        display: 'flex', 
        gap: '1rem', 
        justifyContent: 'center', 
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        <a 
          href="https://www.planetarytech.com/"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-block',
            padding: '0.75rem 1.25rem',
            backgroundColor: '#007bff',
            color: 'white',
            textDecoration: 'none',
            borderRadius: '6px',
            fontWeight: '500',
            fontSize: '1rem',
            transition: 'background-color 0.2s'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#0056b3'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#007bff'}
        >
          Planetary Technologies →
        </a>
        <a 
          href="https://www.carbontosea.org/"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-block',
            padding: '0.75rem 1.25rem',
            backgroundColor: '#17a2b8',
            color: 'white',
            textDecoration: 'none',
            borderRadius: '6px',
            fontWeight: '500',
            fontSize: '1rem',
            transition: 'background-color 0.2s'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#138496'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#17a2b8'}
        >
          Carbon to Sea Initiative →
        </a>
        <a 
          href="https://alkalign.ocean.dal.ca/"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-block',
            padding: '0.75rem 1.25rem',
            backgroundColor: '#28a745',
            color: 'white',
            textDecoration: 'none',
            borderRadius: '6px',
            fontWeight: '500',
            fontSize: '1rem',
            transition: 'background-color 0.2s'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#218838'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#28a745'}
        >
          Ocean Alk-Align →
        </a>
      </div>
    </div>
  )
}

// Main container component that follows Open/Closed Principle
export default function InformationSections() {
  return (
    <>
      {/* Four sections in 2x2 grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', marginBottom: '2rem' }}>
        <PCO2InfoSection />
        <MonitoringImportanceSection />
        <DataSourceSection />
        <OAEInfoSection />
      </div>

      {/* Fifth section - Research Partners */}
      <ResearchPartnersSection />

      {/* Climate importance callout */}
      <div style={{ 
        textAlign: 'center', 
        background: '#fff3cd',
        padding: '1.5rem',
        borderRadius: '8px',
        border: '1px solid #ffc107'
      }}>
        <p style={{ margin: 0, fontSize: '1.1rem' }}>
          <strong>Why This Matters:</strong> Large-scale carbon dioxide removal is essential for meeting 
          Paris Agreement goals. 344 out of 400 IPCC scenarios rely on Negative Emission Technologies.
        </p>
      </div>
    </>
  )
}